import { useAuth0 } from '@auth0/auth0-react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import MenuIcon from '@mui/icons-material/Menu'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import TodayIcon from '@mui/icons-material/Today'
import AppBar from '@mui/material/AppBar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useScrollTrigger from '@mui/material/useScrollTrigger'

import packageInfo from '../../package.json'
import { useProfile } from '../account/hooks/useProfile'
import { isProfileIncomplete } from '../account/util/profile-util'
import { useAuthenticatedUser } from '../auth/hooks/useAuthenticatedUser'
import { useUserParam } from '../auth/hooks/useUserParam'
import { getVersion } from '../config'
import { getAuth0Options } from '../gateways/auth0'
import { Link } from '../router/components/Link'
import { useLocation } from '../router/hooks/useLocation'
import { useNavigate } from '../router/hooks/useNavigate'
import { CapoLogo } from '../theme/components/CapoLogo'
import { usePageInfo } from '../theme/hooks/usePageInfo'

import { AansluitingMenu } from './components/AansluitingMenu'
import { HoofdleidingMenu } from './components/HoofdleidingMenu'
import { HoofdnetMenu } from './components/HoofdnetMenu'
import { KetensamenwerkingMenu } from './components/KetensamenwerkingMenu'
import { Profile } from './components/Profile'
import { useReleaseNotes } from './hooks/useReleaseNotes'

export const Navigation = () => {
  const { logout } = useAuth0()
  const options = getAuth0Options()
  const returnTo = options.returnTo || '/'
  const [anchorEl, setAnchorEl] = useState(null)
  const [drawer, setDrawer] = useState(false)
  const open = Boolean(anchorEl)
  const trigger = useScrollTrigger({ threshold: 0, disableHysteresis: true })
  const sha = getVersion()
  const { canViewBeheer, canViewProjecten } = useAuthenticatedUser()
  const { userParam, storeUserParam } = useUserParam()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: profile } = useProfile()
  const profileIncomplete = isProfileIncomplete(profile)
  const { unseen: releaseNotesUnseen, markSeen: markReleaseNotesSeen } =
    useReleaseNotes()
  const accountHint = [
    profileIncomplete && 'Accountgegevens aanvullen',
    releaseNotesUnseen && 'Nieuwe release notes',
  ]
    .filter(Boolean)
    .join('\n')
  const { setBreadcrumbsSlot } = usePageInfo()

  const handleChangeUserParam = (value: string) => {
    storeUserParam(value)
    queryClient.refetchQueries()
  }

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const toggleSideList = (open: boolean) => () => {
    setDrawer(open)
  }

  return (
    <AppBar
      position="sticky"
      sx={
        trigger
          ? { background: 'white', elevation: 4 }
          : { background: 'transparent', boxShadow: 'none' }
      }
      id={trigger ? 'elevated' : 'flat'}
    >
      <Toolbar sx={{ color: 'black', gap: 2 }}>
        <IconButton
          data-testid="menuButton"
          edge="start"
          color="inherit"
          onClick={toggleSideList(true)}
        >
          <MenuIcon />
        </IconButton>

        <CapoLogo
          onClick={pathname !== '/home' ? () => navigate('/home') : undefined}
        />

        <Box
          ref={setBreadcrumbsSlot}
          sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}
        />

        <Tooltip
          title={accountHint}
          slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
        >
          <IconButton
            color="inherit"
            onClick={handleMenu}
            data-testid="accountButton"
          >
            <Badge variant="dot" color="error" invisible={!accountHint}>
              <AccountCircleIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Popover
          data-testid="accountPopover"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Profile
            onChangeUserParam={handleChangeUserParam}
            userParam={userParam}
            releaseNotesUnseen={releaseNotesUnseen}
            profileIncomplete={profileIncomplete}
            onReleaseNotesClick={markReleaseNotesSeen}
          />
        </Popover>
      </Toolbar>

      <Drawer
        open={drawer}
        onClose={toggleSideList(false)}
        data-testid="drawer"
        sx={{
          '& .MuiDrawer-paper': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        }}
      >
        <Box sx={{ width: '250px' }} role="presentation">
          <CapoLogo
            onClick={
              pathname !== '/home'
                ? () => {
                    navigate('/home')
                    setDrawer(false)
                  }
                : undefined
            }
            sx={{ margin: '20px 25px' }}
          />

          <AansluitingMenu onItemClick={toggleSideList(false)} />

          <HoofdleidingMenu onItemClick={toggleSideList(false)} />

          <HoofdnetMenu onItemClick={toggleSideList(false)} />

          <KetensamenwerkingMenu onItemClick={toggleSideList(false)} />

          <List>
            {canViewBeheer && (
              <ListItemButton
                component={Link}
                to="/beheer"
                onClick={toggleSideList(false)}
              >
                <ListItemIcon sx={{ color: 'black' }}>
                  <TodayIcon />
                </ListItemIcon>
                <ListItemText primary="Beheer" />
              </ListItemButton>
            )}

            {canViewProjecten && (
              <ListItemButton
                component={Link}
                to="/planning"
                onClick={toggleSideList(false)}
              >
                <ListItemIcon sx={{ color: 'black' }}>
                  <CalendarMonthIcon />
                </ListItemIcon>
                <ListItemText primary="Geblokkeerde weken" />
              </ListItemButton>
            )}

            <ListItemButton
              component={'a'}
              rel="noopener noreferrer"
              href="https://mijnaansluiting.atlassian.net/wiki/spaces/Wiki/pages/39911528/CAPO+UI+Handleiding"
              target="_blank"
              data-testid="handleidingLink"
            >
              <ListItemIcon sx={{ color: 'black' }}>
                <MenuBookIcon />
              </ListItemIcon>
              <ListItemText primary="Handleiding" />
            </ListItemButton>

            <ListItemButton
              onClick={() => logout({ logoutParams: { returnTo } })}
            >
              <ListItemIcon sx={{ color: 'black' }}>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Uitloggen" />
            </ListItemButton>
          </List>
        </Box>

        <Typography
          variant="caption"
          sx={{
            my: '10px',
          }}
        >{`v${packageInfo.version}${sha}`}</Typography>
      </Drawer>
    </AppBar>
  )
}
